import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { getApiErrorMessage, magicFlowApi } from 'src/services';
import { showToastError } from 'src/utilities/errortoast';
import { showToastSuccess } from 'src/utilities/toast';
import type {
  FlowEvent,
  NamespaceOption,
  Namespace,
  PublishMagicFlowEventPayload,
} from 'src/models';

type UseMagicFlowEventsControllerParams = {
  orgUuid?: string;
  hasOrgSelected: boolean;
  scopedHeaders: Record<string, string | boolean>;
};

type NamespaceListResponse = {
  namespaces?: Namespace[];
  page?: number;
  page_size?: number;
  total?: number;
};

type EventListResponse = {
  events?: FlowEvent[];
  page?: number;
  page_size?: number;
  total?: number;
};

const namespacesToOptions = (
  namespaces: Namespace[] | undefined
): NamespaceOption[] => {
  return (namespaces || [])
    .filter(
      (namespace): namespace is Namespace =>
        Boolean(namespace?.namespace_id) && Boolean(namespace?.name)
    )
    .map((namespace) => ({
      value: String(namespace.namespace_id),
      label: String(namespace.name),
    }));
};

export const useMagicFlowEventsController = ({
  orgUuid,
  hasOrgSelected,
  scopedHeaders,
}: UseMagicFlowEventsControllerParams) => {
  const queryClient = useQueryClient();

  const [namespaceId, setNamespaceId] = useState('');
  const [eventName, setEventName] = useState('');
  const [eventId, setEventId] = useState('');
  const [eventDataJson, setEventDataJson] = useState('');

  const [eventsPage, setEventsPage] = useState(1);
  const [eventsPageSize, setEventsPageSize] = useState(20);
  const [eventsStatusFilter, setEventsStatusFilter] = useState<string>('');
  const [eventsNameFilter, setEventsNameFilter] = useState<string>('');
  const [eventsNamespaceFilter, setEventsNamespaceFilter] =
    useState<string>('');

  const [lastPublishResponse, setLastPublishResponse] = useState<any | null>(
    null
  );

  const { data: namespaceOptions = [], isLoading: isLoadingNamespaces } =
    useQuery({
      queryKey: ['magicflow-namespaces', orgUuid],
      enabled: hasOrgSelected,
      queryFn: () =>
        magicFlowApi
          .get<NamespaceListResponse>('/namespace', {
            headers: scopedHeaders,
            params: {
              page: 1,
              page_size: 200,
              sort_by: 'updated_at',
              sort_direction: 'desc',
            },
          })
          .then((res) => namespacesToOptions(res.data?.namespaces)),
    });

  const {
    data: eventListData,
    isLoading: isLoadingEvents,
    isFetching: isFetchingEvents,
    refetch: refetchEvents,
  } = useQuery({
    queryKey: [
      'magicflow-events',
      orgUuid,
      eventsPage,
      eventsPageSize,
      eventsStatusFilter,
      eventsNameFilter,
      eventsNamespaceFilter,
    ],
    enabled: hasOrgSelected,
    queryFn: () =>
      magicFlowApi
        .get<EventListResponse>('/event', {
          headers: scopedHeaders,
          params: {
            page: eventsPage,
            page_size: eventsPageSize,
            ...(eventsStatusFilter ? { status: eventsStatusFilter } : {}),
            ...(eventsNameFilter ? { event_name: eventsNameFilter } : {}),
            ...(eventsNamespaceFilter
              ? { namespace_id: eventsNamespaceFilter }
              : {}),
          },
        })
        .then((res) => res.data),
  });

  const events = eventListData?.events || [];
  const eventsTotal = eventListData?.total ?? 0;

  const publishEventMutation = useMutation({
    mutationFn: (payload: PublishMagicFlowEventPayload) =>
      magicFlowApi.post('/event', payload, { headers: scopedHeaders }),
    onSuccess: (res) => {
      setLastPublishResponse(res.data || null);
      showToastSuccess('Event published successfully');
      setEventId('');
      queryClient.invalidateQueries({
        queryKey: ['magicflow-events', orgUuid],
      });
      queryClient.invalidateQueries({
        queryKey: ['magicflow-flow-runs-list', orgUuid],
      });
    },
    onError: (error) => {
      showToastError(
        getApiErrorMessage(error as any) || 'Failed to publish event'
      );
    },
  });

  const hasPublishErrors = useMemo(() => {
    return !namespaceId.trim() || !eventName.trim() || !eventId.trim();
  }, [eventId, eventName, namespaceId]);

  const handlePublishEvent = () => {
    if (hasPublishErrors) {
      showToastError('Namespace, event name, and event id are required');
      return;
    }

    let parsedEventData: Record<string, unknown> = {};
    try {
      const parsed = JSON.parse(eventDataJson || '{}');
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        parsedEventData = parsed;
      } else {
        showToastError('Event data must be a JSON object');
        return;
      }
    } catch (error) {
      showToastError('Event data must be valid JSON');
      return;
    }

    publishEventMutation.mutate({
      namespace_id: namespaceId.trim(),
      event_name: eventName.trim(),
      event_id: eventId.trim(),
      event_data: parsedEventData,
    });
  };

  return {
    namespaceId,
    setNamespaceId,
    eventName,
    setEventName,
    eventId,
    setEventId,
    eventDataJson,
    setEventDataJson,
    namespaceOptions,
    isLoadingNamespaces,
    hasPublishErrors,
    isPublishingEvent: publishEventMutation.isPending,
    handlePublishEvent,
    lastPublishResponse,
    events,
    eventsTotal,
    isLoadingEvents,
    isFetchingEvents,
    refreshEvents: () => refetchEvents(),
    eventsPage,
    setEventsPage,
    eventsPageSize,
    setEventsPageSize,
    eventsStatusFilter,
    setEventsStatusFilter,
    eventsNameFilter,
    setEventsNameFilter,
    eventsNamespaceFilter,
    setEventsNamespaceFilter,
  };
};
